import React from "react";
import ReactApexChart from "react-apexcharts";
import { DateTime } from "luxon";
import { CITY_KEY_MAP, SERIES_COLOR_MAP, unslugify } from "../../../../constants";

const MultiLineChart = ({ graphData, title, activeSeriesKeys, toggleSeriesKey, selectedLocation, xAxisTitle }) => {

    const filteredSeries = (graphData.series || []).filter(s =>
        activeSeriesKeys.includes(s.name.replace(/\s+/g, "_"))
    );

    // Filter checkbox entries based on selected location
    const cityEntries = selectedLocation && CITY_KEY_MAP[selectedLocation]
        ? Object.entries(CITY_KEY_MAP).filter(([cityName]) => cityName === selectedLocation)
        : Object.entries(CITY_KEY_MAP);

    const chartOptions = {
        chart: {
            height: 350,
            type: "line",
            zoom: { enabled: false },
            toolbar: {
                show: true,
                export: { png: { show: true }, csv: { show: false }, svg: { show: false } }
            }
        },
        stroke: { curve: "smooth", width: 3 },
        colors: filteredSeries.map(s => SERIES_COLOR_MAP[s.name.replace(/\s+/g, "_")] || "#000000"),
        dataLabels: { enabled: false },
        grid: { row: { colors: ["#f3f3f3", "transparent"], opacity: 0.5 } },
        xaxis: {
            categories: graphData.categories || [],
            labels: { rotate: -45, style: { fontSize: "12px" } },
            title: {
                text: xAxisTitle || '',
                style: { fontSize: '13px', fontWeight: 600, color: '#555' }
            }
        },
        yaxis: {
            min: 0,
            forceNiceScale: true,
            labels: { formatter: val => Math.floor(val) },
            title: {
                text: 'Number of Job Applications',
                style: { fontSize: '13px', fontWeight: 600, color: '#555' }
            }
        },
        tooltip: { 
            x: { format: "MMM" },
            shared: true,
            intersect: false,
            // Show all series in tooltip, including zero values
            custom: function({ series, seriesIndex, dataPointIndex, w }) {
                const categories = w.globals.categoryLabels;
                const seriesNames = w.globals.seriesNames;
                let tooltip = `<div style="padding: 10px; background: #fff; border: 1px solid #ccc; border-radius: 4px;">`;
                tooltip += `<div style="font-weight: 600; margin-bottom: 8px;">${categories[dataPointIndex]}</div>`;
                
                // Show all active series, even if value is 0
                series.forEach((seriesData, idx) => {
                    const value = seriesData[dataPointIndex] !== undefined ? seriesData[dataPointIndex] : 0;
                    const seriesName = seriesNames[idx] || '';
                    const seriesKey = seriesName.replace(/\s+/g, "_");
                    const color = SERIES_COLOR_MAP[seriesKey] || "#000000";
                    tooltip += `<div style="display: flex; align-items: center; margin-bottom: 4px;">
                        <span style="display: inline-block; width: 12px; height: 12px; background-color: ${color}; margin-right: 8px; border-radius: 2px;"></span>
                        <span style="font-size: 12px;">${seriesName}: <strong>${value}</strong></span>
                    </div>`;
                });
                
                tooltip += `</div>`;
                return tooltip;
            }
        },
        legend: {
            show: false
        }
    };

    return (
        <>
            <div>
                {title && <h1 className="heading-sm">{title}</h1>}
                <ReactApexChart options={chartOptions} series={filteredSeries} type="line" height={350} />
            </div>

            <div>
                <div className="grid grid-cols-2 gap-10">
                    {cityEntries
                        .flatMap(([cityName, keys]) => [
                            { label: `Selected Year ${cityName}`, key: keys.current },
                            { label: `Last Year ${cityName}`, key: keys.last },
                        ])
                        .map(({ label, key }) => (
                            <div key={key} className="flex items-center border-b border-gray-300 py-2 gap-3">
                                <span
                                    className="w-4 h-4 rounded "
                                    style={{ backgroundColor: SERIES_COLOR_MAP[key] }}
                                ></span>
                                <input
                                    type="checkbox"
                                    className={`
                                        w-4 h-4 mr-
                                        rounded-lg
                                        accent-primary               
                                        border-2 border-primary
                                        cursor-pointer
                                    `}
                                    checked={activeSeriesKeys.includes(key)}
                                    onChange={() => toggleSeriesKey(key)}
                                />
                                <span className="mr-2 text-sm text-primary">{label}</span>
                            </div>
                        ))}
                </div>
            </div>
        </>
    );
};

export default MultiLineChart;
